var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled from '@emotion/styled';
import { useNavigate, generatePath } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useBundle } from '@amzn/react-arb-tools';
import Thumbnail from '@amzn/meridian/thumbnail';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import { TwisterButton } from 'src/common/buttons';
import { RioText, ScrollableRow, ErrorBar } from 'src/common/styled';
import { routes } from 'src/utils/routes';
import { useDefaultApiParams } from 'src/hooks/useDefaultApiParams';
import { getProductDetails } from 'src/hooks/useProductDetails';
const ThumbnailContainer = styled.div `
  display: flex;
  flex-direction: column;
  gap: 4px;

  /**
   * The first child div holds the border so that it doesn't get affected by the disabled opacity.
   * It also creates the container height/width so that the image shrinks to fit inside it.
   */
  > div {
    position: relative;
    width: 68px;
    height: 68px;
    border-radius: 68px;

    border-color: ${({ theme, isActive }) => {
    if (isActive)
        return theme.rio.semantic.color.background.inverse;
    return theme.rio.base.color.gray[theme.mode === 'dark' ? 1000 : 700];
}};
    border-width: ${({ isActive }) => (isActive ? '2px' : '1px')};
    border-style: ${({ isDisabled, isActive }) => (isDisabled && !isActive ? 'dashed' : 'solid')};

    /**
     * Create a box shadow that lays on top of the border. This helps distinguish the selected border
     * in dark mode
     */
    &:after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      box-shadow: ${({ theme, isActive }) => {
    const color = theme.rio.semantic.color.background.primary;
    const width = isActive ? '3px' : '2px';
    return `0px 0px 0px ${width} ${color} inset`;
}};
      border-radius: 68px;
    }
  }

  div[role='img'] {
    width: 68px;
    height: 68px;
    border-radius: 68px;
    opacity: ${({ isDisabled, isActive }) => (isDisabled && !isActive ? '.5' : '1')};
  }
`;
export const updateButtonStates = (variationIndex, dimensionIndex, isCurrentSelectionDisabled, selection, dimensions, variations) => {
    // Create a deep copy
    const newDimensions = dimensions.map((dimension) => (Object.assign(Object.assign({}, dimension), { values: dimension.values.map((value) => (Object.assign({}, value))) })));
    // Update selected variation isActive value
    newDimensions[dimensionIndex].values[variationIndex].isActive = true;
    const oldVariationIndex = selection[dimensionIndex];
    if (oldVariationIndex !== -1) {
        newDimensions[dimensionIndex].values[oldVariationIndex].isActive = false;
    }
    // Update matrix
    const newSelection = selection.map((value, index) => {
        // Set the new selected value
        if (index === dimensionIndex) {
            return variationIndex;
        }
        // If the current selection was previously disabled, set the rest to an unselected state
        if (isCurrentSelectionDisabled) {
            return -1;
        }
        // Otherwise just return the current value
        return value;
    });
    const isValidSelection = newSelection.every((value) => value !== -1);
    if (!isValidSelection) {
        // Find variations for selected values
        const variationsForSelectedValue = variations.filter((variation) => newSelection.every((value, index) => value === -1 || variation.dimensionValueIndexes[index] === value));
        newSelection.forEach((selectionValue, selectionIndex) => {
            // Find the variation values for the current selection
            const variationValues = variationsForSelectedValue.map((variation) => variation.dimensionValueIndexes[selectionIndex]);
            const dimension = newDimensions[selectionIndex];
            // If the current selection was previously disabled, reset that group to have all values enabled
            if (selectionIndex === dimensionIndex && isCurrentSelectionDisabled) {
                dimension.values.forEach((value) => {
                    value.isDisabled = false;
                });
                // If unselected, reset the state based on the variations for this selection
            }
            else if (selectionValue === -1) {
                dimension.values.forEach((value, vIndex) => {
                    value.isDisabled = !variationValues.includes(vIndex);
                    value.isActive = false;
                });
            }
        });
    }
    return {
        isValidSelection,
        newSelection,
        newDimensions,
    };
};
/**
 * Product selection matrix (aka Twister)
 */
export const ProductSelection = ({ initialSelection = [], variationDimensions = [], variations = [], updateCtaState, }) => {
    const [bundle, isBundleLoading] = useBundle('pages.productDetails');
    const [dimensions, setDimensions] = React.useState(variationDimensions);
    const [selection, setSelections] = React.useState(initialSelection);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { getDefaultParams } = useDefaultApiParams();
    const onSelectClick = (variationIndex, dimensionIndex, isDisabled) => __awaiter(void 0, void 0, void 0, function* () {
        // If already selected, do nothing;
        if (selection[dimensionIndex] === variationIndex) {
            return;
        }
        const { isValidSelection, newDimensions, newSelection } = updateButtonStates(variationIndex, dimensionIndex, isDisabled, selection, dimensions, variations);
        setDimensions(newDimensions);
        setSelections(newSelection);
        updateCtaState(!isValidSelection);
        if (isValidSelection) {
            const targetVariation = variations.find(({ dimensionValueIndexes }) => JSON.stringify(dimensionValueIndexes) === JSON.stringify(newSelection));
            /**
             * If the current selection is "valid", but the variation cannot be found, then we have hit an invalid state.
             * This is generally triggered by the user clicking too fast before a valid query has finished loading. In this
             * case, do not cancel the queries. Let the previous query finish and update the component state with the latest
             * valid selection data.
             */
            if ((targetVariation === null || targetVariation === void 0 ? void 0 : targetVariation.asin) === undefined) {
                // TODO: Error handling
                console.error(`No existing ASIN for selected variation combination: ${newSelection}`);
                return;
            }
            // Cancel the previous queries
            yield queryClient.cancelQueries({ predicate: (query) => query.queryKey.includes('getProductDetails') });
            try {
                yield queryClient.fetchQuery({
                    queryKey: ['getProductDetails', targetVariation.asin, getDefaultParams()],
                    queryFn: getProductDetails,
                    // Data stays fresh for 30 seconds to allow for caching between product selections
                    // TODO: Move away from this approach since AmazonAPI usually says not to add an additional cache
                    // on top of theirs. The risk being that we show outdated offer info for 1 min
                    staleTime: 1 * 30 * 1000,
                });
                const pdpRoute = generatePath(routes.productDetailPage, { asin: targetVariation.asin });
                navigate(`${pdpRoute}${window.location.search}`, { state: { preventScrollReset: true } });
            }
            catch (error) {
                if (error && error.constructor.name === 'CancelledError') {
                    // Let the query be cancelled silently.
                }
                else {
                    // TODO: What do we do if the query fails?
                    console.error('An unexpected error occurred:', error);
                }
            }
        }
    });
    // Update the component state when API updates
    React.useEffect(() => {
        setSelections(initialSelection);
        setDimensions(variationDimensions);
    }, [initialSelection, variationDimensions]);
    // On click - scroll each selection into view
    React.useEffect(() => {
        const selectedElements = document.getElementsByClassName('twister-selected');
        Array.from(selectedElements).forEach((element) => {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        });
    }, [selection]);
    // Initial load - scroll selections into view, but don't scroll the page
    React.useEffect(() => {
        const selectedElements = document.getElementsByClassName('twister-selected');
        Array.from(selectedElements).forEach((element) => {
            element.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'center' });
        });
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [isBundleLoading]);
    if (isBundleLoading)
        return null;
    return (_jsx("div", { "data-testid": "variation-matrix", children: dimensions.map(({ name, values }, dimensionIndex) => {
            var _a;
            return (_jsxs("div", { "data-testid": "dimension-row", children: [_jsxs(Row, { spacingInset: "350 350 none 350", spacing: "300", alignmentVertical: "stretch", children: [selection[dimensionIndex] === -1 && (_jsx(Column, { alignmentVertical: "center", children: _jsx(ErrorBar, { "data-testid": "error-bar" }) })), _jsxs(Text, { type: "b300", "data-testid": "dimension-selection-value", children: [name, ": ", _jsx("b", { children: ((_a = values[selection[dimensionIndex]]) === null || _a === void 0 ? void 0 : _a.value) || bundle.getMessage('selectToBuy') })] })] }), _jsx(ScrollableRow, { spacingInset: "300 350 none 350", spacing: "300", overflowX: "scroll", alignmentVertical: "top", children: values.map((option, index) => option.thumbnailUrl ? (_jsxs(ThumbnailContainer, { role: "button", className: option.isActive ? 'twister-selected' : '', onClick: () => onSelectClick(index, dimensionIndex, option.isDisabled), isDisabled: option.isDisabled, isActive: option.isActive, "data-testid": `twister-image-button${option.isActive ? '-selected' : ''}`, children: [_jsx(Thumbnail, { source: option.thumbnailUrl }), option.isDisabled && (_jsx(RioText, { type: "b100", alignment: "center", rioColor: "secondary", children: bundle.getMessage('unavailable') }))] }, option.value)) : (_jsx(TwisterButton, { className: option.isActive ? 'twister-selected' : '', shape: "small", onClick: () => onSelectClick(index, dimensionIndex, option.isDisabled), isDisabled: option.isDisabled, isActive: option.isActive, "data-testid": `twister-button${option.isActive ? '-selected' : ''}`, children: option.isActive ? _jsx("b", { children: option.value }) : option.value }, option.value))) })] }, name));
        }) }));
};
